import React from 'react';
import theme from './PriceCalculationDisplay.module.scss';
import { Trans } from '@lingui/react/macro';
import { PriceCalculationStep, PriceStepType } from '@/types';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProjectCurrencyDisplay } from '@/seller/components/currency';

interface PriceCalculationDisplayProps {
    value: PriceCalculationStep[];
    loading?: boolean;
}

const PriceCalculationDisplay: React.FC<PriceCalculationDisplayProps> = ({ value, loading = false }) => {
    const hasFeeOrTaxSteps = value.some((x) => [PriceStepType.Tax, PriceStepType.Fee].includes(x.type));

    if (!hasFeeOrTaxSteps) {
        return (
            <div>
                <Trans>No fees and/or VAT setup selected. This will be the price to the buyer</Trans>
            </div>
        );
    }

    const baseStep = value.find((step) => step.type === PriceStepType.Base);
    const basePrice = baseStep?.price || 0;

    const feeSteps = value.filter(
        (step) => step.type === PriceStepType.Fee && step.changedFromPrevious && (step.priceChangeAmount || 0) !== 0
    );

    const finalStep = value.find((step) => step.type === PriceStepType.Final);
    const finalPrice = finalStep?.price || 0;

    const taxSteps = value.filter((step) => step.type === PriceStepType.Tax);

    let vatPercentage = 0;
    let priceBeforeVat = basePrice;

    feeSteps.forEach((step) => (priceBeforeVat += Math.abs(step.priceChangeAmount || 0)));

    if (priceBeforeVat > 0 && finalPrice > priceBeforeVat) {
        vatPercentage = ((finalPrice - priceBeforeVat) / priceBeforeVat) * 100;
        vatPercentage = Number(vatPercentage.toFixed(2));
    }

    const totalVatAmount = finalPrice - priceBeforeVat;

    const subtotal = priceBeforeVat;

    const tableRows = [
        {
            name: 'Base price',
            percent: '-',
            amount: basePrice,
            isBase: true,
        },
    ];

    feeSteps.forEach((step) => {
        const feeChangeAmount = Math.abs(step.priceChangeAmount || 0);
        const feePercent = basePrice > 0 ? (step.priceChangeAmount || 0) / basePrice : 0;

        tableRows.push({
            name: step.name,
            percent: feePercent !== 0 ? `${(feePercent * 100).toFixed(2)}%` : '-',
            amount: feeChangeAmount,
            isBase: false,
        });
    });

    return (
        <div className={theme.container}>
            {loading && (
                <div className={theme.loading}>
                    <FontAwesomeIcon icon={faSpinner} spin />
                </div>
            )}

            <table className={theme.previewTable}>
                <thead>
                    <tr>
                        <th>
                            <Trans>Name</Trans>
                        </th>
                        <th>
                            <Trans>Amount</Trans>
                        </th>
                        <th>
                            <Trans>Fee percentage</Trans>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {tableRows.map((row, index) => (
                        <tr key={index}>
                            <td>{row.isBase ? <Trans>Base price</Trans> : row.name}</td>
                            <td>
                                <ProjectCurrencyDisplay value={row.amount} />
                            </td>
                            <td>{row.percent}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className={theme.summary}>
                <div className={theme.summaryRow}>
                    <span className={theme.summaryLabel}>
                        <Trans>Total excl. VAT</Trans>
                    </span>
                    <span className={theme.summaryValue}>
                        <ProjectCurrencyDisplay value={Math.abs(subtotal)} />
                    </span>
                </div>

                <div className={theme.summaryRow}>
                    <span className={theme.summaryLabel}>
                        <Trans>Total VAT</Trans>
                        {vatPercentage > 0 && ` (${vatPercentage}%)`}
                    </span>
                    <span className={theme.summaryValue}>
                        <ProjectCurrencyDisplay value={Math.abs(totalVatAmount)} />
                    </span>
                </div>

                <div className={`${theme.summaryRow} ${theme.finalPrice}`}>
                    <span className={theme.summaryLabel}>
                        {vatPercentage > 0 ? <Trans>Price for buyer (incl. VAT)</Trans> : <Trans>Price for buyer</Trans>}
                    </span>
                    <span className={theme.summaryValue}>
                        <ProjectCurrencyDisplay value={Math.abs(finalPrice)} />
                    </span>
                </div>
            </div>
        </div>
    );
};

export default PriceCalculationDisplay;
